import { ThemeComponent } from "@/theme/ThemeComponent";
import { BaseComponent, Component, Prop, Ref, SimpleEvent } from "@eastsideco/salvo-ts";
import { AutoresolvingOptionSelector, OptionSelector } from '@/utils/OptionSelectors';
import { ISimpleEvent } from "strongly-typed-events";
import { cart } from "@/theme/Cart";
import { CartMain } from '../Cart/CartMain';
import { SideCart } from '../SideCart/SideCart';

export interface ProductOption extends BaseComponent {
    selected: string | null;
    onChange: ISimpleEvent<string | null>;
    updateBasedOnSelector(selector: OptionSelector): void;
    select(value: string | null): void;
}

interface VariantContent {
    id: number;
    addOns: [
        {
            handle: string;
        }
    ];
}

interface HTMLElementWithVariants extends HTMLElement {
    selectedVariants?: { [key: string]: string | null };
}

@Component
export class ProductOptionPicker extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop variantContent!: VariantContent[];
    @Prop customisetext!: string;

    @Ref option1!: ProductOption;
    @Ref({ required: false }) option2?: ProductOption;
    @Ref({ required: false }) option3?: ProductOption;
    @Ref({ required: false }) selectedValue?: HTMLSpanElement[];
    @Ref({ required: false }) addOn!: HTMLDivElement;

    selector: AutoresolvingOptionSelector | null = null;
    nonSelected = false;

    get selectedVariant(): Shopify.Liquid.Variant | null {
        const options = [
            this.option1?.selected || null,
            this.option2?.selected || null,
            this.option3?.selected || null,
        ];

        const match = this.product.variants.filter((v) => v.option1 == options[0]
            && v.option2 == options[1]
            && v.option3 == options[2])[0];

        return match || null;
    }

    private _onSelectedVariantChange = new SimpleEvent<Shopify.Liquid.Variant | null>();

    public get onSelectedVariantChange() {
        return this._onSelectedVariantChange.asEvent();
    }

    private selectedVariants: { [key: string]: string | null } = {};

    init() {
        this.selector = new AutoresolvingOptionSelector(this.product);

        let oldValue = this.selectedVariant;
        const onAnyChanged = () => {
            const newValue = this.selectedVariant;
            if (newValue != oldValue) {
                this._onSelectedVariantChange.dispatch(newValue);
            }
            oldValue = newValue;
            if (this.selectedValue && this.selectedVariant) {
                for (const [index, option] of this.selectedValue.entries()) {
                    option.textContent = this.selectedVariant.options[index];
                }
            }
            this.renderOptions();
        };


        this.$subscribe(this.option1.onChange, onAnyChanged);
        if (this.option2) {
            this.$subscribe(this.option2.onChange, onAnyChanged);
        }
        if (this.option3) {
            this.$subscribe(this.option3.onChange, onAnyChanged);
        }

        this._onSelectedVariantChange.dispatch(this.selectedVariant);
        this.renderOptions();
    }

    renderOptions() {
        if (!this.selector) {
            return;
        }
        this.selector.select(this.option1.selected, this.option2?.selected || null, this.option3?.selected || null);

        this.option1.updateBasedOnSelector(this.selector);
        this.option2?.updateBasedOnSelector(this.selector);
        this.option3?.updateBasedOnSelector(this.selector);

        // AutoresolvingOptionSelector may modify the values if
        if (this.selector.wasSelectionModified) {
            const selected = this.selector.selected;
            if (this.option1) {
                this.option1.select(selected[0].value);
            }
            if (this.option2) {
                this.option2.select(selected[1].value);
            }
            if (this.option3) {
                this.option3.select(selected[2].value);
            }
            this._onSelectedVariantChange.dispatch(this.selectedVariant);
            this.renderOptions();
        }

        document.dispatchEvent(new Event('buildProdFormSummary'));
        if (!this.addOn) { return; }
        this.addOns();
    }

    private addOns() {
        this.addOn.innerHTML = '';
        const drawer = document.createElement('drawer-container');
        drawer.setAttribute('class', 'drawer');
        drawer.setAttribute('data-open-side', 'right');
        drawer.setAttribute('data-ref', 'drawer');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('use-overlay', 'true');
        drawer.classList.add('product-form__add-on-drawer');

        // Create drawer header
        const drawerHeader = document.createElement('drawer-header');
        drawerHeader.setAttribute('class', 'drawer__header');
        drawerHeader.setAttribute('data-ref', 'drawerHeader');
        drawerHeader.innerHTML = `
            <h5>${this.customisetext}</h4>
            <button class="drawer__close button" type="button" data-ref="closeButton" 
            onclick="
            this.closest('drawer-container').removeAttribute('open'); 
            document.body.classList.remove('drawer-open');"
            >
                <span class="visually-hidden">Close (esc)</span>
                <svg class="icon icon--close" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
                    <path d="m7.71875 6.28125-1.4375 1.4375L23.5625 25 6.28125 
                    42.28125l1.4375 1.4375L25 26.4375l17.28125 17.28125 1.4375-1.4375L26.4375 25 43.71875 
                    7.71875l-1.4375-1.4375L25 23.5625Z" fill="currentColor"></path>
                </svg>
            </button>
        `;

        const toggleButton = document.createElement('button');
        toggleButton.textContent = this.customisetext;
        toggleButton.classList.add('button', 'button--block', 'button--xlarge', 'button--secondary');
        toggleButton.classList.add('product-main__add-on-toggle');
        toggleButton.setAttribute('data-ref', 'drawerToggle');
        // toggleButton.setAttribute('disabled', 'true');
        toggleButton.onclick = () => this.toggleDrawer(drawer); // Set up the click handler

        this.addOn.appendChild(toggleButton);
        this.addOn.appendChild(drawer);
        // Append the drawer header to the drawer
        drawer.appendChild(drawerHeader);

        const addOnItems = this.variantContent?.find((i) => i.id == this.selectedVariant?.id);
        if (!addOnItems?.addOns) { return; }
        for (const item of addOnItems.addOns) {
            fetch(`/products/${item?.handle}/?section_id=product-form-add-ons`)
                .then((response) => response.text())
                .then((text) => {
                    const itemHTML =
                    new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
                    if (itemHTML) {
                        drawer.appendChild(itemHTML);
                    }
                })
                .catch((error) => {
                    if (error instanceof Error) {
                        this.$log.error(error.message);
                    }
                });
        }

        // Create a wrapper div
        const buttonWrapper = document.createElement('div');
        buttonWrapper.classList.add('drawer__button-wrap'); // Add a class to the wrapper if needed
        // Create the "Add to Cart" form
        const addToCartButton = document.createElement('form');
        addToCartButton.setAttribute('action', '/cart/add');
        addToCartButton.setAttribute('method', 'post');
        addToCartButton.setAttribute('enctype', 'multipart/form-data');
        addToCartButton.classList.add('drawer__button', 'options-wrap-js');
        addToCartButton.setAttribute('value', ' ');

        // Create the "Add to Cart" submit button
        const addToCartSubmit = document.createElement('input');
        addToCartSubmit.setAttribute('type', 'submit');
        addToCartSubmit.setAttribute('value', 'Add to Cart');
        addToCartSubmit.classList.add(
            'button',
            'button--block',
            'button--xlarge',
            'button--primary',
            'options-submit-js'
        );
        addToCartSubmit.setAttribute('disabled', 'true');
        addToCartButton.appendChild(addToCartSubmit);

        addToCartButton.onclick = async(e) => {
            e.preventDefault();
            await this.addToCartAddons();
        };
        // Replace the original button with the form
        buttonWrapper.appendChild(addToCartButton);

        // Create the "Clear Options" button
        const clearButton = document.createElement('button');
        clearButton.textContent = 'Clear Options';
        clearButton.classList.add(
            'button',
            'button--block',
            'button--xlarge',
            'button--secondary',
            'drawer__button'
        );
        clearButton.onclick = () => {
            document.dispatchEvent(new Event('clearOptions'));
        };


        // Append the button to the wrapper
        buttonWrapper.appendChild(addToCartButton);
        buttonWrapper.appendChild(clearButton);

        // Append the wrapper div to the drawer
        drawer.appendChild(buttonWrapper);

    }

    toggleDrawer(drawer: HTMLElement) {
        const isOpen = drawer.getAttribute('open') === 'true';
        const bodyOverlay = document.querySelector('.body-overlay');
        if (isOpen) {
            drawer.removeAttribute('open'); // Remove the attribute when closing the drawer
            drawer.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('drawer-open'); // Remove class from body
            if (bodyOverlay) {
                bodyOverlay.classList.remove('visible');
                setTimeout(() => {
                    bodyOverlay.classList.add('hidden');
                }, 300);
            }
        } else {
            drawer.setAttribute('open', 'true'); // Set the attribute when opening the drawer
            drawer.setAttribute('aria-hidden', 'false');
            document.body.classList.add('drawer-open'); // Add class to body
            if (bodyOverlay) {
                bodyOverlay.classList.remove('hidden');
                setTimeout(() => {
                    bodyOverlay.classList.add('visible');
                }, 10);
            }
        }
        // Toggle CSS class for animations
        drawer.classList.toggle('is-open', !isOpen);
    }

    // Sets the visibility of the 'Customise' button
    setAddOnsVisibility() {
        if (this.addOn) {
            const addOnItems = this.variantContent?.find((i: VariantContent) => i.id == this.selectedVariant?.id);
            // If the selected variant has add ons, show the 'Customise' button
            if (addOnItems?.addOns.length) {
                this.addOn.classList.remove('visually-hidden');
            // Otherwise hide it
            } else {
                this.addOn.classList.add('visually-hidden');
            }
        }
    }

    /**
     * Add product to cart.
    */
    protected async addToCartAddons() {
        try {
            const inputs = document.querySelectorAll('.drawer__button-wrap input[type="hidden"]');
            const ids = inputs as NodeListOf<HTMLInputElement>;
            // Prepare items for the cart
            const idsToAdd: { id: number; quantity: number }[] = [];
            const cartCheck: { id: number; inCart: boolean }[] = [];
            for (const [i, id] of ids.entries()) {
                const idFormatted = id.value.replace(/^[^-]+-/, '');
                const idUse = parseInt(idFormatted, 10);
                const itemInCart: boolean = cart.checkCart(idUse);
                const addString = { id: idUse, quantity: 1};
                const checkString = { id: idUse, inCart: itemInCart };
                cartCheck.push(checkString);
                idsToAdd.push(addString);
            }

            await cart.addItems(idsToAdd)
                .then(() => {
                    document.dispatchEvent(new Event('cartChanged'));
                })
                .catch((error: unknown) => {
                    this.$log.error('Cart error', { error });
                });
        } catch (error) {
            this.$log.error('Unexpected cart error', { error });
        }

        document.dispatchEvent(new Event('clearOptions'));

        const openedDrawer = document.querySelector('drawer-container.product-form__add-on-drawer');
        if(openedDrawer) {
            openedDrawer.removeAttribute('open');
        }
        document.body.classList.remove('drawer-open');
        const bodyOverlay = document.querySelector('.body-overlay');
        if (bodyOverlay) {
            bodyOverlay.classList.remove('visible');
            setTimeout(() => {
                bodyOverlay.classList.add('hidden');
            }, 300);
        }

        const targetSummaryItems = document.querySelectorAll(`.product__summary-item--addons`);
        for (const targetSummary of targetSummaryItems){
            targetSummary.remove();
        }
    }
}
